import { joiResolver } from '@hookform/resolvers/joi';
import { DefaultValues, useForm, UseFormReturn } from 'react-hook-form';
import { MutationStatus } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import React, { useMemo } from 'react';
import { usePostNonNationalExamCreateCandidate } from '../../../api-refactored/non-national-exam-registration/usePostNonNationalCreateCandidate';
import { IOptionType } from '../../../api/candidate/exam/model';
import { CandidateDetailsFormData } from '../../../models';
import candidateDetailsSchema from '../schema/candidateDetailsSchema';
import RouteKey from '../../../routes/key';
import { SpersCandidateDetailsLocation } from '../components/nonNationalExam/spers/SpersCandidateDetails';
import { GetNonNationalCandidateDetailsResponse } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalCandidateDetails';

const { examSchoolingDetails } = RouteKey.exam.child;

interface CandidateDetailsFormProps {
  disableValidateSinBirthCertNo: boolean;
  uuid: string;
  lockedByUser?: string;
  candidateDetailsData: GetNonNationalCandidateDetailsResponse | undefined;
  motherTongueLanguagesData: IOptionType[] | undefined;
  setErrorMsg: React.Dispatch<React.SetStateAction<string>>;
}

interface ParticularContinueReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  submitContinue: (submitData: CandidateDetailsFormData) => void;
  /**
   * Form to send edit & delete
   */
  formHooks: UseFormReturn<CandidateDetailsFormData>;
  /**
   * React query mutation object
   */
  nonNationalExamCreateCandidateStatus: MutationStatus;
  nextPage: () => void;
}

/**
 * A hook to handle search functionality
 * @returns
 */

export const useCandidateDetailsForm = ({
  disableValidateSinBirthCertNo,
  uuid,
  lockedByUser,
  candidateDetailsData,
  motherTongueLanguagesData,
  setErrorMsg,
}: CandidateDetailsFormProps): ParticularContinueReturn => {
  const history = useHistory();
  const { state } = useLocation<SpersCandidateDetailsLocation>();

  const motherTongueLanguageToBeTakenDefault = motherTongueLanguagesData?.find(
    (v) => v.code === candidateDetailsData?.motherTongueLanguageToBeTaken
  );

  const formDefaultValues: DefaultValues<CandidateDetailsFormData> | undefined = useMemo(
    () => ({
      name: candidateDetailsData?.declaringPerson.name || '',
      uinFinPassport: candidateDetailsData?.declaringPerson.uinFinPassport || '',
      singaporeBirthCertNo: candidateDetailsData?.singaporeBirthCertNo || '',
      dateOfAttainedSPRStatus: candidateDetailsData?.dateOfAttainedSPRStatus || '',
      motherTongueLanguageToBeTaken: motherTongueLanguageToBeTakenDefault,
    }),
    [candidateDetailsData, motherTongueLanguageToBeTakenDefault]
  );

  const formHooks = useForm<CandidateDetailsFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(candidateDetailsSchema(disableValidateSinBirthCertNo)),
    defaultValues: formDefaultValues,
  });

  const {
    mutate: nonNationalExamCreateCandidateMutate,
    status: nonNationalExamCreateCandidateStatus,
  } = usePostNonNationalExamCreateCandidate({
    uuid,
    onSuccess: () => nextPage(),
    onError: (err) => setErrorMsg(err?.response?.data.message || ''),
  });

  const nextPage = () => {
    const pathname = `${examSchoolingDetails.route}`;
    history.push({
      pathname,
      state: {
        examType: state?.examType,
        examTitle: state.examTitle,
        levelSeekingAdmission: state.levelSeekingAdmission,
        isSupplementary: state.isSupplementary,
      },
    });
  };

  const submitContinue = (submitData: CandidateDetailsFormData) => {
    if (lockedByUser) {
      nextPage();
      return;
    }

    nonNationalExamCreateCandidateMutate({
      dateOfAttainedSPRStatus: disableValidateSinBirthCertNo
        ? submitData.dateOfAttainedSPRStatus
        : '',
      declaringPerson: {
        name: submitData.name,
        uinFinPassport: submitData.uinFinPassport,
      },
      motherTongueLanguageToBeTaken: submitData.motherTongueLanguageToBeTaken.code,
      singaporeBirthCertNo: disableValidateSinBirthCertNo ? '' : submitData.singaporeBirthCertNo,
    });
  };

  return {
    formHooks,
    submitContinue,
    nonNationalExamCreateCandidateStatus,
    nextPage,
  };
};
